import React, { useState } from 'react';
import {
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Icon,
    useTheme,
} from '../../../base';
import {
    MenuWrapper,
    HeaderWrapper,
    AccordionWrapper,
    StepHeaderWrapper,
    StepNameWrapper,
} from './CandidatesMenu.styles';
import { IJobCandidatesByStep } from '../../../common';
import CandidateItem from './CandidateItem';
import { useTranslation } from 'react-i18next';

interface CandidatesMenuProps {
    steps: Array<IJobCandidatesByStep>;
}

const CandidatesMenu = (props: CandidatesMenuProps) => {
    const { steps } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const [expandedStep, setExpandedStep] = useState<string | false>(false);

    const handleAccordionChange =
        (panel: string) =>
        (_event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpandedStep(isExpanded ? panel : false);
        };

    return (
        <MenuWrapper>
            <HeaderWrapper>
                <Typography variant="h4" color="primary">
                    {t('jobApplicationPage.candidatesMenutTitle', {
                        defaultValue: 'Candidates',
                    })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('jobApplicationPage.candidatesMenuSubtitle', {
                        defaultValue: 'View candidates by interview step',
                    })}
                </Typography>
            </HeaderWrapper>

            <AccordionWrapper>
                {steps.map((step) => {
                    const candidatesCount = step.candidates?.length || 0;
                    const stepId = `step-${step.stepIndex}`;

                    return (
                        <Accordion
                            key={stepId}
                            expanded={expandedStep === stepId}
                            onChange={handleAccordionChange(stepId)}
                            elevation={0}
                            disableGutters
                            sx={{
                                '&:before': {
                                    display: 'none',
                                },
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                borderRadius: 0,
                                '&:last-child': {
                                    borderBottom: 'none',
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={
                                    <Icon
                                        name="ExpandMoreOutlinedIcon"
                                        sx={{
                                            color: theme.palette.text.primary,
                                        }}
                                    />
                                }
                                aria-controls={`${stepId}-content`}
                                id={`${stepId}-header`}
                                sx={{
                                    minHeight: '56px',
                                    '&.Mui-expanded': {
                                        minHeight: '56px',
                                    },
                                    '& .MuiAccordionSummary-content': {
                                        margin: theme.spacing(1.5, 0),
                                    },
                                    '&:hover': {
                                        backgroundColor:
                                            theme.palette.action.hover,
                                    },
                                }}
                            >
                                <StepHeaderWrapper>
                                    <StepNameWrapper>
                                        <Typography
                                            variant="h6"
                                            fontWeight={600}
                                        >
                                            {step.stepName}
                                        </Typography>
                                        {candidatesCount > 0 && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                ({candidatesCount})
                                            </Typography>
                                        )}
                                    </StepNameWrapper>
                                </StepHeaderWrapper>
                            </AccordionSummary>
                            <AccordionDetails
                                sx={{
                                    padding: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {candidatesCount === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        textAlign="center"
                                        sx={{ py: 2 }}
                                    >
                                        {t(
                                            'jobApplicationPage.candidatesMenuNoCandidates',
                                            {
                                                defaultValue:
                                                    'No candidates in this step',
                                            }
                                        )}
                                    </Typography>
                                ) : (
                                    step.candidates.map((candidate) => (
                                        <CandidateItem
                                            key={candidate.id}
                                            candidate={{
                                                id: String(
                                                    candidate.candidateId
                                                ),
                                                name: candidate.candidateName,
                                                avatar: '',
                                                decision: null,
                                                isOwner: false,
                                            }}
                                            candidateScore={
                                                candidate.candidateScore
                                            }
                                            candidateTags={candidate.tags}
                                            applicationId={candidate.id}
                                        />
                                    ))
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </AccordionWrapper>
        </MenuWrapper>
    );
};

export default CandidatesMenu;
